import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { EMPTY } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import type { SelectUser } from '@core/models';
import { environment } from '../../../environments/environment';

const STORAGE_KEY = 'auth_user';

/**
 * Session-storage key holding the invitation code while MSAL performs its signup redirect.
 * The redirect reloads the browser, so the code must survive in storage rather than component state.
 */
export const PENDING_INVITATION_CODE_KEY = 'pending_invitation_code';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly msalService = inject(MsalService);
  private readonly broadcastService = inject(MsalBroadcastService);
  private readonly _user = signal<SelectUser | null>(this._loadFromStorage());
  private readonly _isInitializing = signal(true);
  private readonly _authError = signal<string | null>(null);

  readonly user = this._user.asReadonly();
  readonly isInitializing = this._isInitializing.asReadonly();
  readonly authError = this._authError.asReadonly();
  readonly isAuthenticated = computed(() => !!this._user());
  readonly isAdmin = computed(() => this._user()?.role === 'admin');
  readonly isOperator = computed(() => this._user()?.role === 'operator');

  /**
   * Subscribes to MSAL interaction completion to fetch the user profile from /api/auth/me.
   * Must be called once at app startup from AppComponent.
   * Handles the redirect race condition: if router URL is /auth after _setUser, navigate to /.
   */
  initializeMsal(): void {
    void this.msalService.instance.initialize().then(() => {
      this.broadcastService.inProgress$
        .pipe(
          filter(status => status === InteractionStatus.None),
          switchMap(() => {
            const accounts = this.msalService.instance.getAllAccounts();
            if (accounts.length === 0) {
              // No account after MSAL settled (e.g. signup was cancelled). Drop any
              // stale pending invitation code so a later attempt can re-initiate.
              sessionStorage.removeItem(PENDING_INVITATION_CODE_KEY);
              this._clearUser();
              this._isInitializing.set(false);
              return EMPTY;
            }
            this.msalService.instance.setActiveAccount(accounts[0]);

            // If a signup just completed, an invitation code is waiting in session storage.
            // Remove it before dispatching the POST so that a second InteractionStatus.None
            // event (e.g. silent token renewal) cannot trigger a duplicate request.
            const pendingInvitationCode = sessionStorage.getItem(PENDING_INVITATION_CODE_KEY);
            if (pendingInvitationCode) {
              sessionStorage.removeItem(PENDING_INVITATION_CODE_KEY);
              return this.http.post<SelectUser>('/api/auth/invite-accepted', {
                invitationCode: pendingInvitationCode,
              });
            }

            return this.http.get<SelectUser>('/api/auth/me');
          }),
        )
        .subscribe({
          next: (result) => {
            if (result && typeof result === 'object' && 'id' in result) {
              this._authError.set(null);
              this._setUser(result);
              const baseUrl = this.router.url.split('?')[0];
              if (baseUrl.startsWith('/auth') || baseUrl === '/accept-invite') {
                const destination = result.role === 'operator' ? ['/internal/admin'] : ['/'];
                void this.router.navigate(destination).then(() => this._isInitializing.set(false));
                return;
              }
            }
            this._isInitializing.set(false);
          },
          error: (err: unknown) => {
            sessionStorage.removeItem(PENDING_INVITATION_CODE_KEY);
            const code = (err as { error?: { code?: string } })?.error?.code ?? null;
            this._setAuthError(code);
            // Clear MSAL token cache without triggering a logout redirect
            this.msalService.instance.clearCache();
            this._clearUser();
            void this.router.navigate(['/auth']).then(() => this._isInitializing.set(false));
          },
        });
    });
  }

  /**
   * Identity-first (home realm discovery) lookup for the given email — Entra External ID has
   * no built-in HRD. Reports whether the email's tenant is federated and, if so, the Entra
   * `domain_hint` value to pass to {@link login} so Entra routes straight to the external IdP.
   */
  lookupIdentity(email: string) {
    return this.http.get<{ signInType: 'federated' | 'emailaddress'; domainHint: string }>(
      '/api/auth/identity',
      { params: { email } },
    );
  }

  /** Initiates Microsoft SSO login via redirect flow, optionally routing a federated tenant straight to its IdP. */
  login(email: string, domainHint?: string): void {
    this.msalService.loginRedirect({
      scopes: environment.apiScopes,
      // loginHint must be omitted for federated tenants — Entra errors out when domain_hint
      // and loginHint are both present, since loginHint only applies to non-federated sign-in.
      loginHint: domainHint ? undefined : email,
      extraQueryParameters: domainHint ? { domain_hint: domainHint } : undefined,
    }).subscribe(); //one-short observable, no required unsubcribe
  }

  /** Signs out from Entra External ID and clears local user state. */
  logout(): void {
    this._authError.set(null);
    this._clearUser();
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: environment.msalConfig.postLogoutRedirectUri,
    }).subscribe();  //one-short observable, no required unsubcribe
  }

  /** Clears user state, sets a session-expired message, and navigates to /auth. */
  handleSessionExpiry(): void {
    this._authError.set('Your session has expired. Please sign in again.');
    this._clearUser();
    void this.router.navigate(['/auth']);
  }

  getCurrentUser(): SelectUser | null {
    return this._user();
  }

  updateCurrentUser(patch: Partial<SelectUser>): void {
    const current = this._user();
    if (current) this._setUser({ ...current, ...patch });
  }

  private _setAuthError(code: string | null): void {
    if (code === 'Auth.AccountDeactivated') {
      this._authError.set('Your account has been deactivated. Please contact your administrator.');
    } else if (code === 'Auth.NotInvited') {
      this._authError.set('You have not been invited to Horizon Scanning. Please contact your administrator.');
    } else if (code === 'Auth.InvalidInvitationCode') {
      this._authError.set('Your invitation link is invalid. Please use the link from your invitation email or contact your administrator.');
    } else if (code === 'Auth.InvitationCodeExpired') {
      this._authError.set('Your invitation has expired. Please contact your administrator for a new invitation.');
    } else {
      this._authError.set('Authentication failed. Please try again or contact your administrator.');
    }
  }

  private _setUser(user: SelectUser): void {
    this._user.set(user);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  }

  private _clearUser(): void {
    this._user.set(null);
    localStorage.removeItem(STORAGE_KEY);
  }

  private _loadFromStorage(): SelectUser | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as SelectUser) : null;
    } catch {
      return null;
    }
  }
}
